import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { formatRoleLabel } from '../utils/roleLabels';
import InviteUserForm from '../components/InviteUserForm';
import BillingSection from '../components/BillingSection';
import NotificationSettingsSection from '../components/NotificationSettingsSection';
import IntegrationsSection from '../components/IntegrationsSection';
import UpgradeCta from '../components/UpgradeCta';
import { canInviteUsers, getUserCapLabel } from '../utils/planAccess';
import PendingInvitationsTable from '../components/PendingInvitationsTable';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import UserAvatar from '../components/UserAvatar';
import './MyCompany.css';

const COMPANY_TABS = ['company', 'billing', 'team', 'notifications'];

const tabFromHash = () => {
  const hash = (typeof window !== 'undefined' ? window.location.hash : '').replace('#', '');
  if (hash === 'integrations') {
    return 'notifications';
  }
  if (COMPANY_TABS.includes(hash)) {
    return hash;
  }
  return 'company';
};

const MyCompany = () => {
  const { t } = useTranslation(['company', 'common', 'users']);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });
  const [error, setError] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: ''
  });
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'employee',
    isActive: true
  });
  const [userError, setUserError] = useState('');
  const [activeTab, setActiveTab] = useState(tabFromHash);

  useEffect(() => {
    const onHashChange = () => setActiveTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const selectTab = (tab) => {
    setActiveTab(tab);
    setEditing(false);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${tab}`);
    }
  };

  useEffect(() => {
    if (!user) {
      return;
    }

    const activeCompanyId = user.active_company_id || user.company_id;
    if (!activeCompanyId) {
      setError(t('notAssigned'));
      setLoading(false);
      return;
    }

    fetchCompany(activeCompanyId);
    fetchCompanyUsers();
    fetchPendingInvitations();
  }, [user, t]);

  const fetchCompany = async (companyId) => {
    try {
      const response = await axios.get(`/api/companies/${companyId}`);
      const companyData = response.data.data;
      setCompany(companyData);
      setFormData({
        name: companyData.name || '',
        address: companyData.address || '',
        phone: companyData.phone || '',
        email: companyData.email || ''
      });
    } catch (error) {
      console.error('Error fetching company:', error);
      setError(formatError(error) || t('failedFetch'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setCompanyUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching company users:', error);
    }
  };

  const fetchPendingInvitations = async () => {
    const activeCompanyId = user?.active_company_id || user?.company_id;
    if (!activeCompanyId) {
      return;
    }
    try {
      const response = await axios.get(`/api/companies/${activeCompanyId}/invitations`);
      setPendingInvitations(response.data.data || []);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
    }
  };

  const handleUserEdit = (userToEdit) => {
    setEditingUser(userToEdit);
    setUserFormData({
      email: userToEdit.email || '',
      password: '',
      firstName: userToEdit.first_name || '',
      lastName: userToEdit.last_name || '',
      phone: userToEdit.phone || '',
      role: userToEdit.role || 'employee',
      isActive: userToEdit.is_active !== false
    });
    setUserError('');
    setShowUserModal(true);
  };

  const handleUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setUserError('');

    if (!editingUser) {
      return;
    }

    try {
      const payload = new FormData();
      payload.append('email', userFormData.email);
      payload.append('firstName', userFormData.firstName);
      payload.append('lastName', userFormData.lastName);
      payload.append('phone', userFormData.phone || '');
      payload.append('role', userFormData.role);
      payload.append('isActive', userFormData.isActive);
      if (userFormData.password) {
        payload.append('password', userFormData.password);
      }
      const activeCompanyId = user.active_company_id || user.company_id;
      payload.append('companyId', activeCompanyId);

      await axios.put(`/api/users/${editingUser.id}`, payload);
      setShowUserModal(false);
      setEditingUser(null);
      fetchCompanyUsers();
    } catch (error) {
      setUserError(formatError(error) || t('failedUpdateUser'));
    }
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setUserError('');
    setUserFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      role: 'employee',
      isActive: true
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!company) {
      return;
    }

    try {
      await axios.put(`/api/companies/${company.id}`, formData);
      setEditing(false);
      fetchCompany(company.id);
    } catch (error) {
      setError(formatError(error) || t('failedUpdate'));
    }
  };

  const handleCancel = () => {
    setEditing(false);
    if (company) {
      setFormData({
        name: company.name || '',
        address: company.address || '',
        phone: company.phone || '',
        email: company.email || ''
      });
    }
  };

  const handleInviteChange = (e) => {
    setInviteForm({
      ...inviteForm,
      [e.target.name]: e.target.value
    });
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    if (!company) {
      setInviteError(t('companyNotLoaded'));
      return;
    }

    try {
      const response = await axios.post(`/api/companies/${company.id}/invite`, inviteForm);
      const data = response.data.data;

      if (data.message) {
        setInviteSuccess(t('inviteSent', { email: data.email || inviteForm.email }));
      }

      setInviteForm({
        email: ''
      });
      fetchCompany(company.id);
      fetchCompanyUsers();
      fetchPendingInvitations();
    } catch (error) {
      setInviteError(formatError(error) || t('failedInvite'));
    }
  };

  const handleInvitationCancelSuccess = (email) => {
    setInviteError('');
    setInviteSuccess(t('inviteCancelled', { email: email || inviteForm.email }));
  };

  const handleInvitationCancelError = (message) => {
    setInviteSuccess('');
    setInviteError(message);
  };

  const handleInvitationResendSuccess = (email) => {
    setInviteError('');
    setInviteSuccess(t('inviteResent', { email: email || inviteForm.email }));
  };

  const handleInvitationResendError = (message) => {
    setInviteSuccess('');
    setInviteError(message || t('failedResendInvite'));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!company) {
    return (
      <div className="container container--wide main-content">
        <div className="error">{t('notFound')}</div>
      </div>
    );
  }

  return (
    <div className="my-company">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('pageTitle')}</h1>
          {activeTab === 'company' && !editing && (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>
              {t('editCompany')}
            </button>
          )}
        </div>

        {error && <div className="error">{error}</div>}

        <div className="my-company-tabs" role="tablist" aria-label={t('pageTitle')}>
          {COMPANY_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`company-tab-${tab}`}
              aria-selected={activeTab === tab}
              aria-controls={`company-panel-${tab}`}
              className={`my-company-tab${activeTab === tab ? ' is-active' : ''}`}
              onClick={() => selectTab(tab)}
            >
              {t(`tab${tab.charAt(0).toUpperCase()}${tab.slice(1)}`)}
            </button>
          ))}
        </div>

        {activeTab === 'company' && (
        <div className="card" role="tabpanel" id="company-panel-company" aria-labelledby="company-tab-company">
          {!editing ? (
            <>
              <div className="company-info">
                <div className="info-row">
                  <strong>{t('infoName')}</strong>
                  <span>{company.name}</span>
                </div>
                {company.email && (
                  <div className="info-row">
                    <strong>{t('infoEmail')}</strong>
                    <span>{company.email}</span>
                  </div>
                )}
                {company.phone && (
                  <div className="info-row">
                    <strong>{t('infoPhone')}</strong>
                    <span>{company.phone}</span>
                  </div>
                )}
                {company.address && (
                  <div className="info-row">
                    <strong>{t('infoAddress')}</strong>
                    <span>{company.address}</span>
                  </div>
                )}
                <div className="info-row">
                  <strong>{t('infoUserCount')}</strong>
                  <span>{company.user_count || 0}</span>
                </div>
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2>{t('editCompanyHeading')}</h2>
              <div className="form-group">
                <label>{t('nameRequired')}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('common:email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>{t('common:phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>{t('addressLabel')}</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  {t('common:update')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                >
                  {t('common:cancel')}
                </button>
              </div>
            </form>
          )}
        </div>
        )}

        {activeTab === 'billing' && (
          <div role="tabpanel" id="company-panel-billing" aria-labelledby="company-tab-billing">
            <BillingSection />
          </div>
        )}

        {activeTab === 'notifications' && (
          <div role="tabpanel" id="company-panel-notifications" aria-labelledby="company-tab-notifications">
            {company && <NotificationSettingsSection companyId={company.id} />}
            <IntegrationsSection />
          </div>
        )}

        {activeTab === 'team' && (
          <div role="tabpanel" id="company-panel-team" aria-labelledby="company-tab-team">
        <InviteUserForm
          email={inviteForm.email}
          onChange={handleInviteChange}
          onSubmit={handleInviteSubmit}
          error={inviteError}
          success={inviteSuccess}
          capLabel={getUserCapLabel(user, t)}
          disabled={!canInviteUsers(user)}
          disabledMessage={!canInviteUsers(user) ? <UpgradeCta /> : null}
        />

        <div className="card">
          <h2>{t('pendingInvitations')}</h2>
          <PendingInvitationsTable
            companyId={company.id}
            invitations={pendingInvitations}
            onRefresh={fetchPendingInvitations}
            onCancelSuccess={handleInvitationCancelSuccess}
            onCancelError={handleInvitationCancelError}
            allowResend
            onResendSuccess={handleInvitationResendSuccess}
            onResendError={handleInvitationResendError}
          />
        </div>

        <div className="card">
          <div className="my-company-card-header">
            <h2>{t('companyUsers')}</h2>
            <Link to="/users" className="btn btn-secondary btn-sm">
              {t('manageUsers')}
            </Link>
          </div>
          {companyUsers.length === 0 ? (
            <EmptyState
              icon="group"
              title={t('noCompanyUsersTitle')}
              description={t('noCompanyUsersDescription')}
              actionLabel={t('manageUsers')}
              onAction={() => navigate('/users')}
            />
          ) : (
            <>
            <table className="table admin-mobile-table">
              <thead>
                <tr>
                  <th>{t('common:name')}</th>
                  <th>{t('common:email')}</th>
                  <th>{t('common:phone')}</th>
                  <th>{t('common:role')}</th>
                  <th>{t('common:status')}</th>
                  <th>{t('common:actions')}</th>
                </tr>
              </thead>
              <tbody>
                {companyUsers.map((companyUser) => (
                  <tr key={companyUser.id} style={{ cursor: 'pointer' }} onClick={() => handleUserEdit(companyUser)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <UserAvatar user={companyUser} size={32} />
                        <span>{companyUser.first_name} {companyUser.last_name}</span>
                      </div>
                    </td>
                    <td>{companyUser.email}</td>
                    <td>{companyUser.phone || t('common:emptyCell')}</td>
                    <td>{formatRoleLabel(companyUser.role, t)}</td>
                    <td>{companyUser.is_active ? t('common:active') : t('common:inactive')}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleUserEdit(companyUser)}
                      >
                        {t('common:edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="admin-cards-mobile my-company-cards-mobile">
              {companyUsers.map((companyUser) => (
                <div
                  key={companyUser.id}
                  className="sim-task-card admin-mobile-card my-company-mobile-card"
                  onClick={() => handleUserEdit(companyUser)}
                >
                  <div className="my-company-mobile-card-name">
                    <UserAvatar user={companyUser} size={32} />
                    <span>{companyUser.first_name} {companyUser.last_name}</span>
                  </div>
                  <p className="admin-mobile-card-meta">{companyUser.email}</p>
                  <p className="admin-mobile-card-meta">{formatRoleLabel(companyUser.role, t)}</p>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
          </div>
        )}

        <Modal
          isOpen={showUserModal}
          onClose={handleCloseUserModal}
          title={t('users:editUser')}
        >
          <form onSubmit={handleUserSubmit}>
                <div className="form-group">
                  <label>{t('settings:firstNameLabel')}</label>
                  <input
                    type="text"
                    name="firstName"
                    value={userFormData.firstName}
                    onChange={handleUserChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings:lastNameLabel')}</label>
                  <input
                    type="text"
                    name="lastName"
                    value={userFormData.lastName}
                    onChange={handleUserChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings:emailLabel')}</label>
                  <input
                    type="email"
                    name="email"
                    value={userFormData.email}
                    onChange={handleUserChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('users:passwordKeepCurrent')}</label>
                  <input
                    type="password"
                    name="password"
                    value={userFormData.password}
                    onChange={handleUserChange}
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings:phoneLabel')}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={userFormData.phone}
                    onChange={handleUserChange}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common:role')}</label>
                  <select
                    name="role"
                    value={userFormData.role}
                    onChange={handleUserChange}
                  >
                    <option value="employee">{formatRoleLabel('employee', t)}</option>
                    <option value="admin">{formatRoleLabel('admin', t)}</option>
                    <option value="reader">{formatRoleLabel('reader', t)}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={userFormData.isActive}
                      onChange={handleUserChange}
                    />{' '}
                    {t('common:active')}
                  </label>
                </div>
                {userError && <div className="error">{userError}</div>}
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    {t('common:update')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseUserModal}
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
        </Modal>
      </div>
    </div>
  );
};

export default MyCompany;
